import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Form, ListGroup, Spinner } from 'react-bootstrap';
import PropTypes from 'prop-types';
import CenteredModal from '../../components/widgets/modal/Modal';
import axios from '../../services/axios';

const UNCHANGED = '__unchanged__';
const NO_TAG = '__no_tag__';
const CHUNK_SIZE = 5;

const buildReference = (slug, id) =>
    `M360-${slug.toUpperCase()}-${String(id).padStart(4, '0')}`;

const OperationInscriptionsBulkActionModal = ({
    mode,
    slug,
    inscriptions,
    excludedCount = 0,
    inscriptionStatus,
    injectionFailedStatus,
    availableTagOperations,
    ACTIONS,
    isClientKnown,
    onClose,
    onRemoveInscription,
    onStatusChanged,
    onTagChanged,
    onActionCompleted,
}) => {
    const [step, setStep] = useState('select');
    const [statut, setStatut] = useState(UNCHANGED);
    const [tagOperation, setTagOperation] = useState(UNCHANGED);
    const [sending, setSending] = useState(false);
    const [results, setResults] = useState([]);
    const [processedCount, setProcessedCount] = useState(null);
    const resultsContainerRef = useRef(null);

    useEffect(() => {
        if (!resultsContainerRef.current) return;
        resultsContainerRef.current.scrollTop = resultsContainerRef.current.scrollHeight;
    }, [results, sending]);

    const inscriptionStatusLabel = Object.fromEntries(inscriptionStatus.map(s => [s.value, s.label]));
    const inscriptionStatusVariant = Object.fromEntries(inscriptionStatus.map(s => [s.value, s.variant ?? 'secondary']));
    const tagOperationsByUuid = Object.fromEntries((availableTagOperations ?? []).map(tag => [tag.uniqueId, tag]));

    const inscriptionLabel = (inscription) =>
        `${inscription.etablissementNom} (${buildReference(slug, inscription.id)})`;

    const pushResult = (result) => setResults(prev => [...prev, result]);

    const closeInscription = (inscription) => {
        const action = ACTIONS?.operations?.getSubEntities?.inscriptions?.close;
        const url = action?.url?.replace('/id', `/${inscription.id}`);
        if (!url) {
            pushResult({ id: inscription.id, name: inscriptionLabel(inscription), isOK: false, msg: 'Action indisponible' });
            return Promise.resolve();
        }
        return axios.post(url, { csrfToken: action.csrfToken })
            .then((data) => {
                if (data?.statut) onStatusChanged?.(inscription.id, data.statut);
                pushResult({ id: inscription.id, name: inscriptionLabel(inscription), isOK: true, msg: 'Inscription clôturée' });
            })
            .catch((err) => {
                // Only the backend's 500 (failClose) actually persists the injection-failed status;
                // any other error code leaves the inscription's real status untouched.
                if (err?.status === 500 && injectionFailedStatus) onStatusChanged?.(inscription.id, injectionFailedStatus);
                pushResult({ id: inscription.id, name: inscriptionLabel(inscription), isOK: false, msg: 'Erreur lors de la clôture' });
            });
    };

    const editInscription = (inscription) => {
        const action = ACTIONS?.operations?.getSubEntities?.inscriptions?.update;
        const url = action?.url?.replace('/id', `/${inscription.id}`);
        if (!url) {
            pushResult({ id: inscription.id, name: inscriptionLabel(inscription), isOK: false, msg: 'Action indisponible' });
            return Promise.resolve();
        }

        const newTag = tagOperation === NO_TAG ? null : tagOperation;
        const body = { csrfToken: action.csrfToken };
        if (statut !== UNCHANGED) body.statut = statut;
        if (tagOperation !== UNCHANGED) body.tagOperation = newTag;

        return axios.post(url, body)
            .then(() => {
                if (statut !== UNCHANGED) onStatusChanged?.(inscription.id, statut);
                if (tagOperation !== UNCHANGED) onTagChanged?.(inscription.id, newTag);
                pushResult({ id: inscription.id, name: inscriptionLabel(inscription), isOK: true, msg: 'Inscription mise à jour' });
            })
            .catch(() => pushResult({ id: inscription.id, name: inscriptionLabel(inscription), isOK: false, msg: 'Erreur lors de la mise à jour' }));
    };

    const runAction = () => {
        setStep('results');
        setSending(true);
        setResults([]);
        setProcessedCount(inscriptions.length);

        const processInscription = mode === 'close' ? closeInscription : editInscription;

        const processByChunks = (remaining) => {
            if (remaining.length === 0) {
                setSending(false);
                onActionCompleted?.();
                return;
            }
            Promise.all(remaining.slice(0, CHUNK_SIZE).map(processInscription))
                .then(() => processByChunks(remaining.slice(CHUNK_SIZE)));
        };

        processByChunks(inscriptions);
    };

    const renderInscriptionRow = (inscription, withRemove) => (
        <ListGroup.Item key={inscription.id} className="d-flex justify-content-between align-items-center">
            <div>
                <div>
                    {inscription.etablissementNom}
                    {inscription.uai && <small className="ms-2 text-muted">{inscription.uai}</small>}
                    <small className="ms-2 text-muted">{buildReference(slug, inscription.id)}</small>
                </div>
                <small className="text-muted">
                    {inscription.etablissementCp} {inscription.etablissementVille}
                </small>
            </div>
            <div className="d-flex align-items-center gap-2">
                <Badge bg={inscriptionStatusVariant[inscription.statut] ?? 'secondary'}>
                    {inscriptionStatusLabel[inscription.statut] ?? inscription.statut}
                </Badge>
                {inscription.tagOperation
                    ? <Badge bg="secondary">{tagOperationsByUuid[inscription.tagOperation]?.name ?? inscription.tagOperation}</Badge>
                    : <Badge bg="light" text="dark">— Aucun tag —</Badge>
                }
                {mode === 'close' && isClientKnown && (
                    isClientKnown(inscription)
                        ? <Badge bg="primary">Mise à jour client</Badge>
                        : <Badge bg="success">Création client</Badge>
                )}
                {withRemove && (
                    <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => onRemoveInscription(inscription.id)}
                        title="Retirer de la sélection"
                    >
                        <i className="fas fa-times" />
                    </Button>
                )}
            </div>
        </ListGroup.Item>
    );

    const inscriptionsList = (withRemove) => (
        <ListGroup className="mb-4" style={{ maxHeight: 300, overflowY: 'auto' }}>
            {inscriptions.map(inscription => renderInscriptionRow(inscription, withRemove))}
        </ListGroup>
    );

    const selectStepContent = mode === 'close'
        ? (
            <>
                {inscriptionsList(true)}
                {excludedCount > 0 && (
                    <Alert variant="warning">
                        {excludedCount > 1
                            ? `${excludedCount} inscriptions ont été retirées car ne sont pas approuvées ou ne disposent pas d'un tag opération`
                            : `${excludedCount} inscription a été retirée car n'est pas approuvée ou ne dispose pas d'un tag opération`
                        }
                    </Alert>
                )}
            </>
        )
        : (
            <>
                {inscriptionsList(true)}
                {excludedCount > 0 && (
                    <Alert variant="warning">
                        {excludedCount > 1
                            ? `${excludedCount} inscriptions ont été retirées car leur statut ne permet plus de modification`
                            : `${excludedCount} inscription a été retirée car son statut ne permet plus de modification`
                        }
                    </Alert>
                )}

                <Form.Group className="mb-3">
                    <Form.Label>Statut</Form.Label>
                    <Form.Select value={statut} onChange={e => setStatut(e.target.value)}>
                        <option value={UNCHANGED}>— Ne pas modifier —</option>
                        {inscriptionStatus.filter(s => s.updatable !== false).map(s => (
                            <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                    </Form.Select>
                </Form.Group>

                <Form.Group>
                    <Form.Label>Tag opération</Form.Label>
                    <Form.Select value={tagOperation} onChange={e => setTagOperation(e.target.value)}>
                        <option value={UNCHANGED}>— Ne pas modifier —</option>
                        <option value={NO_TAG}>— Aucun tag —</option>
                        {availableTagOperations.map(tag => (
                            <option key={tag.uniqueId} value={tag.uniqueId}>{tag.name}</option>
                        ))}
                    </Form.Select>
                </Form.Group>
            </>
        );

    const createCount = isClientKnown ? inscriptions.filter(inscription => !isClientKnown(inscription)).length : 0;
    const knownCount = isClientKnown ? inscriptions.length - createCount : 0;

    const confirmStepContent = mode === 'close'
        ? (
            <>
                {inscriptionsList(false)}
                <Alert variant="info">
                    <div>Attention, une fois envoyée{inscriptions.length > 1 ? 's ces ' : ' cette '}inscription{inscriptions.length > 1 ? 's ne seront plus modifiables' : ' ne sera plus modifiable'}.</div>
                    {isClientKnown && createCount > 0 && <div>{createCount} nouveau{createCount > 1 ? 'x' : ''} client{createCount > 1 ? 's' : ''} seront créés sur Pipedrive</div>}
                    {isClientKnown && knownCount > 0 && <div>{knownCount} client{knownCount > 1 ? 's' : ''} existant{knownCount > 1 ? 's' : ''} seront mis à jour sur Pipedrive et Backoffice</div>}
                </Alert>
            </>
        )
        : (
            <>
                {inscriptionsList(false)}

                <div className="mb-2">
                    <strong>Nouveau statut : </strong>
                    {statut === UNCHANGED
                        ? <span className="text-muted">Inchangé</span>
                        : <Badge bg={inscriptionStatusVariant[statut] ?? 'secondary'}>{inscriptionStatusLabel[statut] ?? statut}</Badge>
                    }
                </div>
                <div>
                    <strong>Nouveau tag opération : </strong>
                    {tagOperation === UNCHANGED
                        ? <span className="text-muted">Inchangé</span>
                        : tagOperation === NO_TAG
                            ? <Badge bg="light" text="dark">— Aucun tag —</Badge>
                            : <Badge bg="secondary">{tagOperationsByUuid[tagOperation]?.name ?? tagOperation}</Badge>
                    }
                </div>
            </>
        );

    const resultsStepContent = (
        <ListGroup ref={resultsContainerRef} style={{ maxHeight: 300, overflowY: 'auto' }}>
            {results.map(result => (
                <ListGroup.Item key={result.id} className="d-flex justify-content-between align-items-center">
                    {result.name}
                    <span className={`text-${result.isOK ? 'success' : 'danger'}`}>
                        <i className={`fas ${result.isOK ? 'fa-check' : 'fa-times'} me-1`} />
                        {result.msg}
                    </span>
                </ListGroup.Item>
            ))}
            {sending && (
                <ListGroup.Item>
                    Traitement en cours <Spinner size="sm" animation="border" className="ms-2" />
                </ListGroup.Item>
            )}
        </ListGroup>
    );

    const stepContent = { select: selectStepContent, confirm: confirmStepContent, results: resultsStepContent }[step];
    const headerCount = processedCount ?? inscriptions.length;

    return (
        <CenteredModal
            show
            size="lg"
            isSending={sending}
            onHide={onClose}
            header={mode === 'close'
                ? `Clôturer ${headerCount} inscription${headerCount > 1 ? 's' : ''}`
                : `Modifier ${headerCount} inscription${headerCount > 1 ? 's' : ''}`
            }
            content={stepContent}
            validatebutton={step === 'select' ? 'Suivant' : step === 'confirm' ? 'Valider' : null}
            validate={step === 'select' ? () => setStep('confirm') : step === 'confirm' ? runAction : null}
            validateDisabled={step === 'select' && inscriptions.length === 0}
            cancelbutton={step === 'select' ? 'Annuler' : step === 'confirm' ? 'Retour' : 'Fermer'}
            cancel={step === 'select' ? onClose : step === 'confirm' ? () => setStep('select') : onClose}
        />
    );
};

OperationInscriptionsBulkActionModal.propTypes = {
    mode: PropTypes.oneOf(['edit', 'close']).isRequired,
    slug: PropTypes.string.isRequired,
    inscriptions: PropTypes.array.isRequired,
    excludedCount: PropTypes.number,
    inscriptionStatus: PropTypes.array.isRequired,
    injectionFailedStatus: PropTypes.string,
    availableTagOperations: PropTypes.array,
    ACTIONS: PropTypes.object,
    isClientKnown: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    onRemoveInscription: PropTypes.func,
    onStatusChanged: PropTypes.func,
    onTagChanged: PropTypes.func,
    onActionCompleted: PropTypes.func,
};

export default OperationInscriptionsBulkActionModal;
